"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import {
  GraduationCap,
  BookOpen,
  Users,
  Heart,
  Globe,
  Mail,
  ChevronDown,
  type LucideIcon,
} from "lucide-react";
import { motion } from "framer-motion";
import { useMetadata } from "@/hooks/useMetadata";
import { apiFetch } from "@/lib/api";
import ScrollWorld, { type ScrollWorldScene } from "./ScrollWorld";
import VideoBackdrop from "./VideoBackdrop";
import SiteFooter from "@/components/SiteFooter";

type IconComponent = React.FC<{
  size?: number;
  strokeWidth?: number;
  className?: string;
}>;

const iconMap: Record<string, IconComponent> = {
  compass: GraduationCap,
  brain: BookOpen,
  layers: Users,
  spark: Globe,
};

// Fallback copy — only shown if the backend is unreachable (e.g. the new
// /about endpoint is not yet deployed). Numbers are NEVER hardcoded; they come
// from the database via /about (or /landing/stats as a secondary source).
const FALLBACK = {
  brand: "UniUI",
  hero: {
    eyebrow: "About UniUI",
    title: "The AI Academic Companion",
    tagline:
      "Built for African university students — launching this semester at FUTO, with expansion across African universities to follow — turn your notes, past questions, and textbooks into precise, source-cited answers in the structured Nigerian-professor format.",
  },
  mission: {
    title: "Our Mission",
    content:
      "UniUI exists to close the gap between generic AI tools and the real academic needs of African university students. We are launching this semester at FUTO, with expansion across African universities to follow. We ingest lecture notes, past questions, and textbooks, then return precise, source-cited answers in the structured Nigerian-professor format — so every student, in any country and at any level, gets personalised, curriculum-aligned support.",
  },
  goals: {
    title: "Our Goals",
    items: [
      {
        icon: "compass",
        title: "Cover every African campus",
        description:
          "From FUTO to the Université de Paris, we are mapping curricula across African universities — launching this semester at FUTO, with expansion across African universities to follow.",
      },
      {
        icon: "brain",
        title: "Local languages & syllabi",
        description:
          "Answers in the language and format your lecturers actually use — French, English, and pidgin-aware.",
      },
      {
        icon: "layers",
        title: "Open to every student",
        description:
          "A free academic tier in every market, so cost is never the reason a student falls behind.",
      },
      {
        icon: "spark",
        title: "Resilient by design",
        description:
          "Dozens of models cascade behind every answer, keeping the continent's study companion online.",
      },
    ],
  },
  vision: {
    title: "Our Vision",
    content:
      "A single, trusted academic companion for all of Africa — where any student, anywhere from Cairo to Cape Town, Lagos to Nairobi, can open UniUI and get precise, cited, curriculum-true help.",
  },
  team: {
    title: "Built by students, for students",
    note: "Made anonymously by a student collective — no egos, just better study tools.",
    community: "African Student Community",
    communityNote:
      "Relentlessly tested and shaped by students across African universities — beginning with the Federal University of Technology, Owerri.",
  },
  contact: {
    title: "Get in Touch",
    subtitle:
      "We build with student feedback. Your suggestions shape our roadmap.",
    email: "support@uniui.com.ng",
    website: "https://uniui.com.ng",
    socials: [
      {
        label: "Website",
        handle: "uniui.com.ng",
        href: "https://uniui.com.ng",
        icon: "globe",
      },
      {
        label: "Email",
        handle: "support@uniui.com.ng",
        href: "mailto:support@uniui.com.ng",
        icon: "mail",
      },
      {
        label: "X",
        handle: "@1stuniui_com_ng",
        href: "https://x.com/1stuniui_com_ng",
        icon: "x",
      },
      {
        label: "TikTok",
        handle: "@1stuniui_com_ng",
        href: "https://tiktok.com/@1stuniui_com_ng",
        icon: "tiktok",
      },
      {
        label: "Instagram",
        handle: "@uniui",
        href: "https://instagram.com/uniui",
        icon: "instagram",
      },
    ] as { label: string; handle: string; href: string; icon: string }[],
  },
};

type Scene = ScrollWorldScene;
type Pillar = { icon: string; title: string; description: string };
type Goal = { icon: string; title: string; description: string };
type StatCard = { key: string; label: string; value: number | null };
type AboutData = {
  brand?: string;
  hero: { eyebrow: string; title: string; tagline: string };
  stats: StatCard[];
  scenes: Scene[];
  mission: { title: string; content: string };
  goals?: { title: string; items: Goal[] };
  vision?: { title: string; content: string };
  pillars: Pillar[];
  team: {
    title: string;
    note: string;
    community: string;
    communityNote: string;
  };
  contact: {
    title: string;
    subtitle: string;
    email: string;
    website: string;
    socials: { label: string; handle: string; href: string; icon: string }[];
  };
};

function formatCount(v: number) {
  return new Intl.NumberFormat("en-NG").format(v || 0);
}
function statText(v: number | null | undefined) {
  return v == null ? "—" : `${formatCount(v)}+`;
}

export default function AboutPage() {
  useMetadata({ key: "about" });
  const [data, setData] = useState<AboutData | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "fallback">(
    "loading",
  );

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const d = await apiFetch<AboutData>("/api/v1/about");
        if (cancelled) return;
        setData(d);
        setState("ready");
      } catch {
        // Secondary_source_for_the_live_numbers, then fall back to static copy.
        try {
          const s = await apiFetch<{
            people?: number;
            courses?: number;
            chunks?: number;
            students?: number;
          }>("/api/v1/landing/stats");
          if (cancelled) return;
          setData({
            brand: FALLBACK.brand,
            hero: FALLBACK.hero,
            stats: [
              {
                key: "students",
                label: "Active Scholars",
                value: s.students ?? s.people ?? null,
              },
              {
                key: "courses",
                label: "Courses Indexed",
                value: s.courses ?? null,
              },
              {
                key: "chunks",
                label: "Enriched Chunks",
                value: s.chunks ?? null,
              },
              {
                key: "people",
                label: "Community Members",
                value: s.people ?? null,
              },
            ],
            scenes: [],
            mission: FALLBACK.mission,
            goals: FALLBACK.goals,
            vision: FALLBACK.vision,
            pillars: [],
            team: FALLBACK.team,
            contact: FALLBACK.contact,
          });
          setState("fallback");
        } catch {
          if (!cancelled) {
            setData(null);
            setState("fallback");
          }
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const scenes = data?.scenes ?? [];
  const pillars = data?.pillars ?? [];
  const socials = data?.contact?.socials ?? FALLBACK.contact.socials;
  const statCards = data?.stats ?? [];
  const mission = data?.mission ?? FALLBACK.mission;
  const team = data?.team ?? FALLBACK.team;
  const goals = data?.goals ?? FALLBACK.goals;
  const vision = data?.vision ?? FALLBACK.vision;
  const brand = data?.brand ?? FALLBACK.brand;

  // Render immediately — no_auth_gate, so the route is public.
  return (
    <div className="relative z-0 min-h-screen bg-background text-foreground">
      {/* -- Single animated "video" backdrop, fixed behind the whole top -- */}
      <VideoBackdrop className="pointer-events-none fixed inset-0 z-0" />

      {/* -- BOLD UniUI wordmark hero (transparent, sits on the fixed backdrop) -- */}
      <section className="relative h-[88vh] min-h-[560px] w-full overflow-hidden flex items-center justify-center">
        <div className="relative z-10 text-center px-6">
          <div className="flex items-center justify-center gap-2 text-y mb-4">
            <GraduationCap className="w-6 h-6" />
            <span className="text-xs font-black uppercase tracking-[0.28em]">
              About {brand}
            </span>
          </div>
          <h1 className="display font-black tracking-tight leading-[0.9] text-6xl md:text-8xl lg:text-9xl text-foreground">
            {brand}
          </h1>
          <p className="mt-6 max-w-2xl mx-auto text-base md:text-lg font-medium text-muted-foreground">
            {data?.hero?.tagline ?? FALLBACK.hero.tagline}
          </p>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-y animate-bounce">
          <ChevronDown className="w-6 h-6" />
        </div>
      </section>

      {/* -- Scroll-world cinematic hero (design ported from oso95/scroll-world) -- */}
      <ScrollWorld />

      <main className="relative z-10 bg-background">
        <div className="max-w-5xl mx-auto px-6 py-16 md:py-24 space-y-20">
          {/* -- Mission -- */}
          <FadeInSection>
            <section className="matte-metal rounded-[2rem] p-8 md:p-12 border border-border/60 relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-y/10 blur-3xl pointer-events-none" />
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-y/10 flex items-center justify-center text-y">
                  <GraduationCap className="w-5 h-5" />
                </div>
                <h2 className="display text-2xl md:text-3xl tracking-tight">
                  {mission.title}
                </h2>
              </div>
              <p className="text-base font-medium leading-relaxed text-foreground/80 max-w-3xl">
                {mission.content}
              </p>
            </section>
          </FadeInSection>

          {/* -- Powered by Academic AI -- */}
          {pillars.length > 0 && (
            <FadeInSection delay={0.1}>
              <section className="space-y-8">
                <div className="text-center space-y-2">
                  <h2 className="display text-2xl md:text-3xl tracking-tight">
                    Powered by Academic AI
                  </h2>
                  <p className="text-sm font-medium text-muted-foreground">
                    Four pillars that make every answer useful, not generic.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {pillars.map((pillar, i) => {
                    const Icon = iconMap[pillar.icon] ?? GraduationCap;
                    return (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.08, duration: 0.5 }}
                        className="group p-6 matte-metal rounded-2xl"
                      >
                        <div className="w-12 h-12 rounded-2xl bg-y/10 flex items-center justify-center text-y mb-4 group-hover:scale-105 transition-transform">
                          <Icon size={24} strokeWidth={1.5} />
                        </div>
                        <h3 className="text-base font-bold tracking-tight mb-1.5">
                          {pillar.title}
                        </h3>
                        <p className="text-sm font-medium text-muted-foreground leading-relaxed">
                          {pillar.description}
                        </p>
                      </motion.div>
                    );
                  })}
                </div>
              </section>
            </FadeInSection>
          )}

          {/* -- Stats band (live DB numbers) -- */}
          {statCards.length > 0 && (
            <FadeInSection delay={0.05}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {statCards.map((s) => (
                  <div
                    key={s.key}
                    className="rounded-2xl border border-border/60 bg-card/60 backdrop-blur-sm p-5 text-center"
                  >
                    <div className="display text-3xl md:text-4xl text-y">
                      {statText(s.value)}
                    </div>
                    <p className="text-xs font-black uppercase tracking-widest text-muted-foreground mt-1">
                      {s.label}
                    </p>
                  </div>
                ))}
              </div>
            </FadeInSection>
          )}

          {/* -- Goals (cover all of Africa) -- */}
          {goals.items && goals.items.length > 0 && (
            <FadeInSection delay={0.1}>
              <section className="space-y-8">
                <div className="text-center space-y-2">
                  <div className="flex items-center justify-center gap-2 text-y mb-1">
                    <Globe className="w-5 h-5" />
                  </div>
                  <h2 className="display text-2xl md:text-3xl tracking-tight">
                    {goals.title}
                  </h2>
                  <p className="text-sm font-medium text-muted-foreground">
                    Built for the whole continent — not one campus.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {goals.items.map((g, i) => {
                    const Icon = iconMap[g.icon] ?? GraduationCap;
                    return (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.08, duration: 0.5 }}
                        className="group p-6 matte-metal rounded-2xl"
                      >
                        <div className="w-12 h-12 rounded-2xl bg-y/10 flex items-center justify-center text-y mb-4 group-hover:scale-105 transition-transform">
                          <Icon size={24} strokeWidth={1.5} />
                        </div>
                        <h3 className="text-base font-bold tracking-tight mb-1.5">
                          {g.title}
                        </h3>
                        <p className="text-sm font-medium text-muted-foreground leading-relaxed">
                          {g.description}
                        </p>
                      </motion.div>
                    );
                  })}
                </div>
              </section>
            </FadeInSection>
          )}

          {/* -- Vision -- */}
          {vision && (
            <FadeInSection delay={0.1}>
              <section className="matte-metal rounded-[2rem] p-8 md:p-12 relative overflow-hidden">
                <div className="absolute -top-12 -right-10 w-48 h-48 rounded-full bg-y/10 blur-3xl pointer-events-none" />
                <div className="relative max-w-3xl mx-auto text-center space-y-4">
                  <span className="inline-flex items-center gap-2 text-y text-xs font-black uppercase tracking-widest">
                    <Globe className="w-4 h-4" /> {vision.title}
                  </span>
                  <p className="text-lg md:text-xl font-semibold leading-relaxed text-foreground/90">
                    {vision.content}
                  </p>
                </div>
              </section>
            </FadeInSection>
          )}

          {/* -- Community (anonymous) -- */}
          <FadeInSection delay={0.1}>
            <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="rounded-2xl border border-border/60 bg-card/60 backdrop-blur-sm p-8 flex flex-col justify-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-y/20 to-y/5 flex items-center justify-center mb-5">
                  <Users className="w-8 h-8 text-y" />
                </div>
                <h3 className="display text-xl tracking-tight mb-3">
                  {team.title}
                </h3>
                <p className="text-xs font-bold text-y uppercase tracking-widest">
                  {team.note}
                </p>
              </div>

              <div className="rounded-2xl border border-border/60 bg-card/60 backdrop-blur-sm p-8 flex flex-col justify-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-y/20 to-y/5 flex items-center justify-center mb-5">
                  <GraduationCap className="w-8 h-8 text-y" />
                </div>
                <p className="text-sm font-bold uppercase tracking-tight text-foreground/90 mb-1">
                  {team.community}
                </p>
                <p className="text-xs font-medium text-muted-foreground leading-relaxed">
                  {team.communityNote}
                </p>
                <div className="mt-4 pt-4 border-t border-border/60 flex items-center gap-2 text-xs font-medium text-muted-foreground">
                  <Heart className="w-4 h-4 text-y" />
                  Made with love for Nigerian students.
                </div>
              </div>
            </section>
          </FadeInSection>

          {/* -- Contact (socials under uniui.com.ng) -- */}
          <FadeInSection delay={0.1}>
            <section className="matte-metal rounded-[2rem] p-8 md:p-12 space-y-6">
              <div className="text-center space-y-2">
                <h2 className="display text-2xl md:text-3xl tracking-tight">
                  {data?.contact?.title ?? FALLBACK.contact.title}
                </h2>
                <p className="text-sm font-medium text-muted-foreground max-w-xl mx-auto">
                  {data?.contact?.subtitle ?? FALLBACK.contact.subtitle}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 flex-wrap">
                <a
                  href={`mailto:${data?.contact?.email ?? FALLBACK.contact.email}`}
                  className="flex items-center gap-2 rounded-xl border border-border bg-background px-5 py-3 text-sm font-bold hover:border-y/40 transition-colors"
                >
                  <Mail className="w-4 h-4 text-y" />
                  {data?.contact?.email ?? FALLBACK.contact.email}
                </a>
                {socials.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-xl border border-border bg-background px-4 py-3 text-sm font-bold hover:border-y/40 transition-colors"
                  >
                    {s.label === "Email" ? (
                      <Mail className="w-4 h-4 text-y" />
                    ) : (
                      <Globe className="w-4 h-4 text-y" />
                    )}
                    {s.label}
                  </a>
                ))}
              </div>
            </section>
          </FadeInSection>

          {/* -- Footer CTA -- */}
          <FadeInSection delay={0.1}>
            <div className="text-center space-y-4 pb-4">
              <div className="inline-flex items-center gap-2 text-y">
                <BookOpen className="w-5 h-5" />
                <span className="display text-lg tracking-tight">UniUI</span>
              </div>
              <p className="text-xs font-medium text-muted-foreground">
                Your AI-powered academic companion.
              </p>
            </div>
          </FadeInSection>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

function FadeInSection({
  children,
  delay = 0,
}: {
  children: React.ReactNode;
  delay?: number;
}) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.section>
  );
}